<template>
  <f7-page
    class="article"
    :theme-dark="dark"
    :class="{ dark }"
  >

    <f7-navbar
      back-link
      transparent
      :theme-dark="dark"
      :title="content && content.title ? content.title : ''"
    />

    <div v-if="content">
      <ArticleGallery
        :poster="marker.image"
        :images="content.images"
      >
      </ArticleGallery>

      <f7-block class="summary">
        <h1>{{ content.title }}</h1>

        <f7-row>
          <f7-col>
            <ArticleBtnPlay
              v-if="marker"
              :autoplay="false"
              :marker="marker"
              :playlist="playlist"
            />
          </f7-col>
          <f7-col>
            <RouteDropdown
              large
              :marker="marker"
            />
          </f7-col>
        </f7-row>

        <ArticleTrack
          v-if="playlist.length"
          :marker="marker"
          :playlist="playlist"
          :track="1"
        >
          <ArticleText
            v-if="content.summary"
            :title="content.title"
            :text="content.summary"
            :track="1"
            :poiId="marker._id"
          />
        </ArticleTrack>
      </f7-block>

      <f7-block>
        <LocationMap
          :lat="Number(marker.coords[1])"
          :lng="Number(marker.coords[0])"
        />
      </f7-block>

      <div
        v-for="(p, i) in content.content"
        :key="'a' + i"
        class="chapter"
      >
        <f7-block>
          <h2>
            {{ p.title }}
          </h2>
          <ArticleTrack
            v-if="playlist.length && p.content"
            :marker="marker"
            :playlist="playlist"
            :track="p.track"
          >
            <ArticleText
              :title="p.title"
              :text="p.content"
              :track="p.track"
              :poiId="marker._id"
            />
          </ArticleTrack>
        </f7-block>
        <f7-list
          v-if="p.items.length"
          accordion-list
        >
          <f7-list-item
            v-for="(item, j) in p.items"
            :key="'c' + i + '-' + j"
            accordion-item
            opened
            :data-track="item.track"
            :class="{ reading: readingTrack(item.track) }"
            :title="item.title"
          >
            <f7-accordion-content>
              <f7-block>
                <ArticleTrack
                  v-if="playlist.length && item.content"
                  :marker="marker"
                  :playlist="playlist"
                  :track="item.track"
                >
                  <ArticleText
                    :title="item.title"
                    :text="item.content"
                    :track="item.track"
                    :poiId="marker._id"
                  />
                </ArticleTrack>
              </f7-block>
            </f7-accordion-content>
          </f7-list-item>
        </f7-list>
      </div>

      <div class="url">
        <a @click="url">
          © Wikipedia
        </a>
      </div>
    </div>

    <f7-fab
      id="btn-backtotop"
      position="right-bottom"
      slot="fixed"
      color="#32AFE3"
      @click="backtopFunction()"
    >
      <f7-icon icon="icon-back"></f7-icon>
    </f7-fab>
  </f7-page>
</template>

<script>
import { mapGetters } from 'vuex'
import RouteDropdown from '@/components/io/RouteDropdown'
import ArticleBtnPlay from '@/components/hl/ArticleBtnPlay'
import ArticleTrack from '@/components/hl/ArticleTrack'
import ArticleText from '@/components/hl/ArticleText'
import ArticleGallery from '@/components/ui/ArticleGallery'
import LocationMap from '@/components/hl/LocationMap'
import { article } from '@/services/atlastories'

export default {
  name: 'Article',
  components: {
    RouteDropdown,
    ArticleTrack,
    ArticleBtnPlay,
    ArticleGallery,
    ArticleText,
    LocationMap
  },
  data () {
    return {
      marker: null,
      content: null,
      playlist: []
    }
  },
  computed: {
    ...mapGetters({
      markers: 'global/markers',
      markerSpeech: 'speech/markerSpeech',
      routeMarker: 'global/routeMarker',
      currentPoiId: 'speech/currentPoiId',
      currentIndex: 'speech/currentIndex',
      routePolyline: 'global/routePolyline',
      highlight: 'global/highlight'
    })
  },
  methods: {
    scrollFunction (scrollTop) {
      let mybutton = document.getElementById('btn-backtotop')
      if (scrollTop > 20) {
        mybutton.style.display = 'block'
      } else {
        mybutton.style.display = 'none'
      }
    },
    backtopFunction () {
      this.Dom7('.page-content').scrollTop(0, 0)
    },
    getMarker () {
      const id = this.$f7route.params.marker
      const marker = this.markers.find(m => m._id === id)
      if (marker) {
        this.marker = marker
        this.getContent(marker.wiki)
      } else {
        if (this.markerSpeech._id === id) this.marker = this.markerSpeech
        if (this.routeMarker._id === id) this.marker = this.routeMarker
        this.getContent(marker.wiki)
      }
    },
    getContent (wiki) {
      this.$f7.preloader.show()
      article(wiki).then(res => {
        this.content = res.data
        const initialTracks = res.data.summary ? [{ subtitle: res.data.title, text: res.data.title + '... ' + res.data.summary }] : []
        const contentWithTracks = res.data.content.slice().reduce((o, chapter) => {
          let track = 0
          if (chapter.content) {
            o.tracks.push({ subtitle: chapter.title, text: chapter.title + '... ' + chapter.content })
            track = o.tracks.length
          }
          const items = chapter.items.reduce((pWithTracks, paragraph) => {
            if (paragraph.content) {
              o.tracks.push({ subtitle: paragraph.title, text: paragraph.title + '... ' + paragraph.content })
              const index = o.tracks.length
              const paragraphWithTrack = Object.assign({}, paragraph, { track: index })
              pWithTracks.push(paragraphWithTrack)
            }
            return pWithTracks
          }, [])
          const chapterWithTrack = Object.assign({}, chapter, { track, items })
          o.content.push(chapterWithTrack)
          return o
        }, { tracks: initialTracks, content: [] })
        this.playlist = contentWithTracks.tracks
        this.content.content = contentWithTracks.content
      }).finally(() => {
        this.$f7.preloader.hide()
      })
    },
    url () {
      if (this.content.url) window.open(this.content.url, '_blank')
    },
    readingTrack (n) {
      return this.marker ? this.currentPoiId === this.marker._id && this.currentIndex === n - 1 : false
    }
  },
  created () {
    this.getMarker()
  },
  mounted () {
    this.$f7.$('.page-content').on('scroll', (e) => {
      this.scrollFunction(e.srcElement.scrollTop)
    })
  }
}
</script>

<style lang="scss">
.article {
  .page-content {
    background: white;
  }
  &.dark {
    .page-content {
      background: black;
    }
    .list.accordion-list ul {
      background: black !important;
    }
  }
  .block {
    margin: 16px 0 32px;
  }
  h1 {
    font-size: 30px;
    margin-bottom: 0;
  }
  h2 {
    font-weight: lighter;
    margin-top: 40px;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-size: 17px;
  }
  .summary {
    font-size: 17px;
    h1 {
      line-height: 1.1em;
      margin-bottom: 17px;
    }
    .article-track-number {
      margin-right: 17px;
    }
  }
  .chapter {
    .block,
    .text {
      margin-bottom: 16px;
    }
    .list {
      margin-top: 0;
    }
    .accordion-item.reading {
      .item-title:before {
        content: "";
        display: inline-block;
        vertical-align: baseline;
        width: 12px;
        height: 12px;
        margin-right: 6px;
        border-radius: 6px;
        background: var(--f7-theme-color);
      }
    }
    .item-title {
      font-weight: normal;
      font-family: var(--f7-font-family);
    }
    & > div:last-of-type {
      margin-bottom: 32px;
    }
  }
  .url {
    padding: 16px;
    text-align: right;
  }
  .article-cta {
    position: relative;
    padding-left: 40px;
    .flat-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translate(0, -50%);
    }
  }
}

#btn-backtotop {
  transform: rotate(0.25turn);
  display: none; /* Hidden by default */
}
</style>
